/**
 * Cómo se nombra un aplicador en pantalla (F3, 26-09-2026): la misma línea en
 * «Empresas de servicios», en el «⋯» de crear tarea y en el perfil.
 */
import i18next, { type TFunction } from "i18next";
import { licenseExpiresOn } from "@/lib/applicators/applicatorRules";
import type { CompanyApplicator } from "@/lib/applicators/applicatorApi";
import type { ApplicationChoiceOption } from "@/components/applicationContext/ApplicationChoiceSheet";

/**
 * 'AAAA-MM-DD' → fecha corta en el idioma de la app. Sin `@/lib/dateFormatters`
 * a propósito: este módulo lo carga crear tarea y el registro manual (por el
 * «⋯»), y aquel arrastra `@/i18n/config` a sus pruebas de render.
 */
export function shortIsoDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  try {
    return new Intl.DateTimeFormat(i18next.language || undefined, {
      timeZone: "UTC",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  } catch {
    return `${m[3]}/${m[2]}/${m[1]}`;
  }
}

/** «Cualificado · ROPO 1234 · caduca 03/05/2031». */
export function licenseLine(
  a: Pick<CompanyApplicator, "license_type" | "ropo_number" | "license_issued_on" | "license_expires_on">,
  t: TFunction,
): string {
  const expiry = licenseExpiresOn(a.license_issued_on ?? null, a.license_expires_on ?? null);
  return [
    a.license_type ? t(`applicationContext.applicators.licenseTypes.${a.license_type}`) : null,
    a.ropo_number ? `ROPO ${a.ropo_number}` : null,
    expiry ? t("applicationContext.applicators.expiresShort", { date: shortIsoDate(expiry) }) : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Los aplicadores como líneas del selector del «⋯». */
export function applicatorChoiceOptions(list: CompanyApplicator[], t: TFunction): ApplicationChoiceOption[] {
  return list.map((a) => ({
    id: a.id,
    label: a.display_name,
    detail: [
      a.kind === "service_company" ? t("applicationContext.applicators.serviceCompanyShort") : null,
      licenseLine(a, t) || null,
    ]
      .filter(Boolean)
      .join(" · "),
  }));
}
